import { useEffect, useState, useRef } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { UserCircle, Trophy, Medal, Flame, Dumbbell, Target, Camera, Trash2, Loader2, Lock } from 'lucide-react';

export default function AlunoPerfilPage() {
  const { user, setUser } = useAuth();
  const fileRef = useRef(null);
  const [data, setData] = useState(null);
  const [ranking, setRanking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Imagem muito grande. Envie até 2MB.');
      return;
    }

    setUploading(true);

    try {
      const fd = new FormData();
      fd.append('file', file);

      const res = await api.post('/auth/upload-avatar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setUser(prev => ({ ...prev, avatar_url: res.data.avatar_url }));
      toast.success('Foto atualizada!');
    } catch (err) {
      toast.error(formatApiError(err));
    }

    setUploading(false);

    if (fileRef.current) fileRef.current.value = '';
  };

  const handleRemove = async () => {
    setUploading(true);

    try {
      await api.delete('/auth/upload-avatar');
      setUser(prev => ({ ...prev, avatar_url: '' }));
      toast.success('Foto removida');
    } catch {
      toast.error('Erro ao remover foto');
    }

    setUploading(false);
  };

  useEffect(() => {
    Promise.all([
      api.get('/aluno/dashboard'),
      api.get('/aluno/ranking')
    ])
      .then(([d, r]) => {
        setData(d.data);
        setRanking(r.data);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="text-zinc-500">Carregando perfil do atleta...</div>;
  }

  const aluno = data?.aluno || {};
  const medalhas = data?.gamificacao?.medalhas || [];
  const streak = data?.gamificacao?.streak || 0;

  // XP vem do backend: mesma fonte usada pelo ranking.
  const xp = Number(data?.xp_total || 0);
  const nivel = Math.max(1, Math.floor(xp / 200) + 1);
  const xpNivelAtual = xp % 200;
  const progresso = Math.min(100, Math.round((xpNivelAtual / 200) * 100));

  return (
    <div className="space-y-6 animate-fadeIn">
      <Card className="bg-gradient-to-br from-[#141414] to-[#0A0A0A] border-[#27272A] overflow-hidden">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-5">
            <div className="w-24 h-24 rounded-3xl bg-[#007AFF]/10 border border-[#007AFF]/20 flex items-center justify-center overflow-hidden shrink-0">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="Foto do atleta" className="w-full h-full object-cover" />
              ) : (
                <UserCircle className="w-14 h-14 text-[#60A5FA]" />
              )}
            </div>

            <div className="flex-1">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">Perfil do Atleta</p>

              <h1 className="text-4xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white mt-1">
                {aluno.name || 'Atleta'}
              </h1>

              <p className="text-sm text-zinc-500">{aluno.email}</p>

              <div className="flex flex-wrap gap-2 mt-3">
                <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20">
                  <Flame className="w-3.5 h-3.5 mr-1" />
                  {xp} XP
                </Badge>

                <Badge className="bg-[#007AFF]/10 text-[#60A5FA] border-[#007AFF]/20">
                  Nível {nivel}
                </Badge>

                <Badge className="bg-green-500/10 text-green-400 border-green-500/20">
                  {data?.inadimplente ? 'Pendente' : 'Em dia'}
                </Badge>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp" onChange={handleUpload} className="hidden" />

                <Button size="sm" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} className="border-[#27272A] text-zinc-300 hover:bg-[#1C1C1E] gap-2">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                  {user?.avatar_url ? 'Trocar foto' : 'Enviar foto'}
                </Button>

                {user?.avatar_url && (
                  <Button size="sm" variant="ghost" disabled={uploading} onClick={handleRemove} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 gap-2">
                    <Trash2 className="w-3.5 h-3.5" /> Remover
                  </Button>
                )}

                <Button size="sm" variant="ghost" onClick={() => window.location.href = '/aluno/alterar-senha'} className="text-zinc-300 hover:text-white gap-2">
                  <Lock className="w-3.5 h-3.5" /> Alterar senha
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between text-xs text-zinc-500 mb-2">
              <span>Progresso para o próximo nível</span>
              <span>{xpNivelAtual}/200 XP</span>
            </div>

            <div className="h-3 bg-[#0A0A0A] border border-[#27272A] rounded-full overflow-hidden">
              <div className="h-full bg-yellow-400 rounded-full transition-all" style={{ width: `${progresso}%` }} />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-5">
            <Trophy className="w-5 h-5 text-yellow-400 mb-2" />
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">Ranking turma</p>
            <h2 className="text-3xl font-black font-['Barlow_Condensed'] text-white mt-1">
              #{ranking?.posicao_turma || '-'}
            </h2>
          </CardContent>
        </Card>

        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-5">
            <Trophy className="w-5 h-5 text-yellow-400 mb-2" />
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">Ranking geral</p>
            <h2 className="text-3xl font-black font-['Barlow_Condensed'] text-white mt-1">
              #{ranking?.posicao_geral || '-'}
            </h2>
          </CardContent>
        </Card>

        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-5">
            <Dumbbell className="w-5 h-5 text-green-400 mb-2" />
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">Sequência</p>
            <h2 className="text-3xl font-black font-['Barlow_Condensed'] text-white mt-1">
              {streak}
            </h2>
            <p className="text-xs text-zinc-500">treino(s)</p>
          </CardContent>
        </Card>

        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-5">
            <Medal className="w-5 h-5 text-yellow-400 mb-2" />
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">Medalhas</p>
            <h2 className="text-3xl font-black font-['Barlow_Condensed'] text-white mt-1">
              {medalhas.length}
            </h2>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <Target className="w-5 h-5 text-[#60A5FA]" />
            <div>
              <h2 className="text-xl font-black font-['Barlow_Condensed'] uppercase text-white">
                Estatísticas do Atleta
              </h2>
              <p className="text-sm text-zinc-500">Resumo da sua evolução no clube.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4">
              <p className="text-zinc-500">Presenças totais</p>
              <p className="text-white text-2xl font-bold">{data?.total_presencas || 0}</p>
            </div>

            <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4">
              <p className="text-zinc-500">Presença no mês</p>
              <p className="text-white text-2xl font-bold">{data?.pct_presenca || 0}%</p>
            </div>

            <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4">
              <p className="text-zinc-500">Contrato</p>
              <p className="text-white text-2xl font-bold">{aluno?.contrato_aceito ? 'Assinado' : 'Pendente'}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
